import React, { useContext, useEffect, useState } from "react";
import ServiceSelection from "./components/ServiceSelection/ServiceSelection";
import SlotSelection from "./components/SlotSelection/SlotSelection";
import UserInformation from "./components/UserInformation/UserInformation";
import AppointmentConfirmation from "./components/AppointmentConfirmation/AppointmentConfirmation";
import { createAppointment } from "../../network/api/users/appointment/appointment";
import DateUtils from "../../utils/DateUtils";
import { toast } from "react-toastify";
import { useLoader } from "../../context/LoadingContext";
import { AppContext } from "../../context/AppContext";
import Modal from "../../components/Modal";
import { WhatsAppButton, WhatsAppCard } from "../../components/WhatsappComponent";
import { Appointment, IDoctor, Service, Slot } from "../../dto/interfaces";
import { logAppointmentEvent } from "../../services/analytics";
import { AppointmentAnalyticsChannel, AppointmentAnalyticsEvent } from "../../dto/analyticsDto";

type Step = "Service Selection" | "Choose a Slot" | "User Information" | "Confirmation";

const steps: Step[] = ["Service Selection", "Choose a Slot", "User Information", "Confirmation"];

interface DoctorProfileParams {
    doctor: IDoctor;
}

const Booking: React.FC<DoctorProfileParams> = ({ doctor }) => {
    const { alias, id: doctorId, name: doctorName } = doctor;
    const enableWhatsapp = (import.meta as any).env.VITE_ENABLE_WHATSAPP_INFO == "true";

    const { setUserToken } = useContext(AppContext);
    const [currentStep, setCurrentStep] = useState<number>(1);

    const [services, setServices] = useState<Service[]>([]);
    const [selectedService, setSelectedService] = useState<Service | null>(null);

    const [slots, setSlots] = useState<Slot[]>([]);
    const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
    const [selectedDate, setSelectedDate] = useState<string>(DateUtils.getISODate(new Date()) || "");

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState<{
        countryCode: string;
        phoneNumber: string;
    }>({ countryCode: "+91", phoneNumber: "" });

    const [error, setError] = useState<{
        name: string;
        email: string;
        phone: string;
    }>({
        name: "",
        email: "",
        phone: ""
    });

    const [isSuccess, setIsSuccess] = useState(false);
    const [openBookingModal, setOpenBookingModal] = useState(false);
    const [appointmentId, setAppointmentId] = useState("");

    const { setLoading } = useLoader();

    const handleNext = async () => {
        if (currentStep == 3) {
            let errorCount = 0;
            let newErrors = { name: "", email: "", phone: "" };
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const phoneRegex = /^\d{10}$/;

            if (!name || name.length < 3) {
                newErrors.name = "Name is invalid";
                errorCount++;
            }

            if (!phone.phoneNumber || !phoneRegex.test(phone.phoneNumber)) {
                newErrors.phone = "Phone is invalid";
                errorCount++;
            }

            setError(newErrors);
            if (errorCount > 0) return;
            setCurrentStep(currentStep + 1);
        } else if (currentStep < steps.length) {
            setCurrentStep(currentStep + 1);
        } else {
            try {
                setLoading(true);
                const res: any = await createAppointment({
                    doctorId: doctorId,
                    userInfo: {
                        name: name,
                        phone: phone.countryCode.slice(1) + phone.phoneNumber,
                        email: email
                    },
                    startTime: selectedSlot?.start,
                    endTime: selectedSlot?.end,
                    typeId: selectedService?.id
                });
                setUserToken(res.token);
                setAppointmentId(res.appointment.id);
                logAppointmentEvent({
                    appointment: res?.appointment as Appointment,
                    action: AppointmentAnalyticsEvent.BOOKED,
                    channel: AppointmentAnalyticsChannel.PATIENT_WEB
                });
                setIsSuccess(true);
            } catch (error) {
                toast.error("An Error Occured " + error.response.data.message);
            } finally {
                setLoading(false);
            }
        }
    };

    const handlePrev = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const prevDisabled = () => {
        return currentStep === 1 || isSuccess;
    };

    const nextDisabled = () => {
        switch (currentStep) {
            case 1: {
                return !Boolean(selectedService);
            }
            case 2: {
                return !Boolean(selectedDate && selectedSlot);
            }
            case 3: {
                return !Boolean(name && phone);
            }
            default:
                return false;
        }
    };

    useEffect(() => {
        // Set the modal button to true if user is on mobile
        // currently commented out as it's not being used
        // if (window.innerWidth < 768) {
        //     enableWhatsapp && setOpenBookingModal(true);
        // }
    }, []);

    return (
        <div className="h-full w-full flex flex-col gap-8 md:gap-16 py-2">
            <div className="flex flex-col items-center w-full flex-1 gap-4 md:gap-8 p-4">
                <div className="w-full max-w-xl flex flex-col gap-4 md:gap-8">
                    <div className="md:hidden flex flex-col items-center gap-4">
                        <WhatsAppButton connectionId={alias} doctorName={doctorName} doctorId={doctorId} />
                    </div>
                    {/* Stepper for larger screens */}
                    <div className="hidden md:flex items-center justify-between md:justify-around w-full">
                        {steps.map((step, index) => (
                            <div key={index} className="flex flex-col items-center">
                                <div
                                    className={`w-8 h-8 sm:w-6 sm:h-6 rounded-full flex items-center justify-center border-2 ${
                                        currentStep === index + 1
                                            ? "border-black bg-black text-white"
                                            : "border-gray-300 bg-white text-gray-400"
                                    }`}
                                >
                                    {index + 1}
                                </div>
                                <p
                                    className={`pt-2 text-xs sm:text-sm ${
                                        currentStep === index + 1 ? "text-black" : "text-gray-400"
                                    }`}
                                >
                                    {step}
                                </p>
                            </div>
                        ))}
                    </div>
                    {/* Step text for smaller screens */}
                    <div className="flex md:hidden justify-center text-lg text-gray-600">or</div>
                </div>

                <div className="w-full max-w-3xl flex flex-col flex-1 border rounded-md shadow-sm gap-8 px-4 py-3">
                    {/* Add page content for each step here */}
                    {/* <p>Content for step: {steps[currentStep - 1]}</p> */}

                    {currentStep === 1 && (
                        <ServiceSelection
                            services={services}
                            selectedService={selectedService}
                            setServices={setServices}
                            setSelectedService={setSelectedService}
                            doctorId={doctorId}
                        />
                    )}

                    {currentStep === 2 && (
                        <SlotSelection
                            serviceId={selectedService?.id || ""}
                            slots={slots}
                            selectedSlot={selectedSlot}
                            setSlots={setSlots}
                            setSelectedSlot={setSelectedSlot}
                            selectedDate={selectedDate}
                            setSelectedDate={setSelectedDate}
                            doctorId={doctorId}
                        />
                    )}

                    {currentStep === 3 && (
                        <UserInformation
                            name={name}
                            setName={setName}
                            phone={phone}
                            setPhone={setPhone}
                            email={email}
                            setEmail={setEmail}
                            error={error}
                        />
                    )}
                    {currentStep === 4 && (
                        <AppointmentConfirmation
                            id={appointmentId}
                            alias={alias}
                            isSuccess={isSuccess}
                            service={selectedService}
                            slot={selectedSlot}
                            name={name}
                            phone={phone}
                            email={email}
                            date={selectedDate}
                        />
                    )}
                </div>

                {!isSuccess && (
                    <div className="flex gap-4 w-full max-w-3xl justify-center">
                        <button
                            onClick={handlePrev}
                            disabled={prevDisabled()}
                            className="px-4 py-2 border rounded-md disabled:opacity-50"
                        >
                            Previous
                        </button>
                        <button
                            onClick={handleNext}
                            disabled={nextDisabled()}
                            className="px-4 py-2 bg-black text-white rounded-md disabled:opacity-50"
                        >
                            {currentStep === steps.length ? "Submit" : "Next"}
                        </button>
                    </div>
                )}
            </div>
            {enableWhatsapp && (
                <>
                    <Modal
                        isOpen={openBookingModal}
                        onClose={() => setOpenBookingModal(false)}
                        title=""
                        onConfirm={() => {}}
                        cancelText=""
                        confirmText=""
                    >
                        <WhatsAppCard connectionId={alias} doctorName={doctorName} doctorId={doctorId} />
                    </Modal>
                </>
            )}
        </div>
    );
};

export default Booking;
